import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';

const { FiZap, FiActivity, FiTarget, FiCheck, FiPlayCircle } = FiIcons;

const IntensitySelector = ({ selectedIntensity, onIntensityChange, disabled, fitnessLevel }) => {
  const { t } = useLanguage();

  const intensityOptions = [
    { value: 'start', label: t('start'), icon: FiPlayCircle, color: 'blue', desc: t('start_desc') },
    { value: 'usor', label: t('easy'), icon: FiActivity, color: 'emerald', desc: t('easy_desc') },
    { value: 'mediu', label: t('medium'), icon: FiZap, color: 'amber', desc: t('medium_desc') },
    { value: 'intens', label: t('intense'), icon: FiTarget, color: 'rose', desc: t('intense_desc') }
  ];

  const getColorClasses = (color, isSelected) => {
    const map = {
      blue: isSelected ? 'bg-blue-50 border-blue-500 text-blue-700 ring-1 ring-blue-200' : 'hover:bg-blue-50/50 hover:border-blue-300 text-slate-600',
      emerald: isSelected ? 'bg-emerald-50 border-emerald-500 text-emerald-700 ring-1 ring-emerald-200' : 'hover:bg-emerald-50/50 hover:border-emerald-300 text-slate-600',
      amber: isSelected ? 'bg-amber-50 border-amber-500 text-amber-700 ring-1 ring-amber-200' : 'hover:bg-amber-50/50 hover:border-amber-300 text-slate-600',
      rose: isSelected ? 'bg-rose-50 border-rose-500 text-rose-700 ring-1 ring-rose-200' : 'hover:bg-rose-50/50 hover:border-rose-300 text-slate-600',
    };
    return map[color];
  };

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <SafeIcon icon={FiZap} className="text-blue-500" /> {t('intensity_title')}
        </h3>
        {disabled && <span className="text-xs font-bold bg-slate-100 text-slate-400 px-2 py-1 rounded">{t('locked')}</span>}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-1 gap-3">
        {intensityOptions.map((option) => (
          <motion.button
            key={option.value}
            whileHover={!disabled ? { scale: 1.01, x: 2 } : {}}
            whileTap={!disabled ? { scale: 0.99 } : {}}
            onClick={() => !disabled && onIntensityChange(option.value)}
            disabled={disabled}
            className={`
              relative w-full p-3 rounded-xl border-2 text-left transition-all duration-200 flex items-center gap-3
              ${getColorClasses(option.color, selectedIntensity === option.value)}
              ${disabled ? 'opacity-50 cursor-not-allowed grayscale' : 'cursor-pointer'}
            `}
          >
            <div className={`p-2 rounded-lg ${selectedIntensity === option.value ? 'bg-white/60' : 'bg-slate-100'} transition-colors`}>
              <SafeIcon icon={option.icon} className="text-lg" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm truncate">{option.label}</div>
              <div className="text-[10px] opacity-80 truncate font-medium">{option.desc}</div>
            </div>
            {selectedIntensity === option.value && (
              <div className="text-current shrink-0">
                <SafeIcon icon={FiCheck} />
              </div>
            )}
          </motion.button>
        ))}
      </div>
    </div>
  );
};

export default IntensitySelector;